import { type FC, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { Box, ButtonProps, Typography } from "@mui/material";
import { AvailabilityTimeBadge } from "./AvailabilityTimeBadge";

type BulkAssignmentBannerProps = {
  onExit: () => void;
  onAssign?: () => void;
  assignDisabled?: boolean;
  preStagedDuration?: number;
  checkedWorkOrdersCount?: number;
};

export const BulkAssignmentBanner: FC<BulkAssignmentBannerProps> = ({
  onExit,
  onAssign,
  assignDisabled = false,
  preStagedDuration = 0,
  checkedWorkOrdersCount = 0,
}) => {
  const { classes, theme } = useStyles();
  const formattedPreStagedDuration = useMemo(() => {
    return Math.round(preStagedDuration) / 60;
  }, [preStagedDuration]);

  return (
    <Box className={classes.root}>
      <Box className={classes.left}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.HD2}
          color={theme.palette.primary.main}
          fontWeight={theme.fontWeight.semiBold}
        >
          Bulk Assignment Mode:
        </Typography>
        <AvailabilityTimeBadge availableTime={formattedPreStagedDuration} />
      </Box>
      <Box className={classes.right}>
        <MiButton
          className={classes.exitButton}
          title="Exit Bulk Assignment"
          variant={BUTTON_VARIANTS.TEXT}
          size={BUTTON_SIZES.SMALL as ButtonProps["size"]}
          color={BUTTON_COLORS.BLACK}
          onClick={onExit}
        />
        <MiButton
          title={`Assign ${
            checkedWorkOrdersCount > 0 ? `(${checkedWorkOrdersCount})` : ""
          }`}
          variant={BUTTON_VARIANTS.CONTAINED}
          color={BUTTON_COLORS.PRIMARY}
          size={BUTTON_SIZES.SMALL as ButtonProps["size"]}
          disabled={assignDisabled}
          onClick={onAssign}
          className={classes.assignButton}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.primary.main}`,
    flexWrap: "wrap",
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  exitButton: {
    textDecoration: "underline",
  },
  assignButton: {
    flex: 1,
  },
}));
